import { format, isValid, parseISO } from "date-fns";
import { ru } from "date-fns/locale";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { PersonData, RISK_LEVELS, PARSER_TYPES } from "@/types/api";

/**
 * Объединение классов с поддержкой условий и оптимизацией Tailwind
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Форматирование даты
 */
export function formatDate(date: string | Date, pattern: string = "dd.MM.yyyy"): string {
  try {
    const dateObj = typeof date === "string" ? parseISO(date) : date;
    if (!isValid(dateObj)) return "";
    return format(dateObj, pattern, { locale: ru });
  } catch {
    return "";
  }
}

/**
 * Форматирование времени
 */
export function formatTime(date: string | Date): string {
  return formatDate(date, "HH:mm:ss");
}

/**
 * Форматирование даты и времени
 */
export function formatDateTime(date: string | Date): string {
  return formatDate(date, "dd.MM.yyyy HH:mm");
}

/**
 * Форматирование суммы в рублях
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Форматирование числа
 */
export function formatNumber(num: number): string {
  return new Intl.NumberFormat("ru-RU").format(num);
}

/**
 * Форматирование процентов
 */
export function formatPercent(value: number): string {
  return `${Math.round(value)}%`;
}

/**
 * Форматирование времени выполнения
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}с`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}м ${remainingSeconds}с` : `${minutes}м`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return minutes > 0 ? `${hours}ч ${minutes}м` : `${hours}ч`;
  }
}

/**
 * Получение названия парсера на русском
 */
export function getParserDisplayName(parserType: string): string {
  const parserNames: Record<string, string> = {
    [PARSER_TYPES.FNS_AU_VIOLATIONS]: "Нарушения АУ ФНС",
    [PARSER_TYPES.FNS_DISQUALIFIED]: "Дисквалифицированные лица ФНС",
    [PARSER_TYPES.FSSP_CRIMINAL_WANTED]: "Криминальный розыск ФССП",
    [PARSER_TYPES.FSSP_DEBTOR_WANTED]: "Розыск должников ФССП",
    [PARSER_TYPES.EFRSB_BANKRUPTCY]: "Банкротство ЕФРСБ",
    [PARSER_TYPES.FNP_PLEDGES]: "Залоги ФНП",
    [PARSER_TYPES.FNS_TAX_DEBTOR]: "Налоговые долги ФНС",
    [PARSER_TYPES.FSSP_IP_DEBTOR]: "Исполнительные производства ФССП",
    [PARSER_TYPES.FSFM_TERRORISTS]: "Террористы и экстремисты",
    [PARSER_TYPES.GOVERNMENT_CORRUPTIONERS]: "Коррупционеры",
  };

  return parserNames[parserType] || parserType;
}

/**
 * Получение цвета для уровня риска
 */
export function getRiskColor(riskLevel: string): string {
  const colors: Record<string, string> = {
    [RISK_LEVELS.LOW]: "text-success-600 bg-success-50",
    [RISK_LEVELS.MEDIUM]: "text-warning-600 bg-warning-50",
    [RISK_LEVELS.HIGH]: "text-error-600 bg-error-50",
  };

  return colors[riskLevel] || "text-ink-2 bg-surface-hover/50";
}

/**
 * Получение текста для уровня риска
 */
export function getRiskText(riskLevel: string): string {
  const texts: Record<string, string> = {
    [RISK_LEVELS.LOW]: "Низкий",
    [RISK_LEVELS.MEDIUM]: "Средний",
    [RISK_LEVELS.HIGH]: "Высокий",
  };

  return texts[riskLevel] || "Неизвестный";
}

/**
 * Валидация ИНН
 */
export function validateINN(inn: string): boolean {
  if (!inn || inn.length !== 12) return false;
  return /^\d{12}$/.test(inn);
}

/**
 * Валидация СНИЛС
 */
export function validateSNILS(snils: string): boolean {
  if (!snils) return false;
  return /^\d{3}-\d{3}-\d{3} \d{2}$/.test(snils);
}

/**
 * Валидация даты рождения
 */
export function validateBirthDate(date: string): boolean {
  if (!date) return false;
  const dateRegex = /^\d{2}\.\d{2}\.\d{4}$/;
  if (!dateRegex.test(date)) return false;

  const [day, month, year] = date.split(".").map(Number);
  const dateObj = new Date(year, month - 1, day);

  return (
    dateObj.getDate() === day &&
    dateObj.getMonth() === month - 1 &&
    dateObj.getFullYear() === year &&
    year >= 1900 &&
    year <= new Date().getFullYear()
  );
}

/**
 * Валидация ФИО
 */
export function validateFullName(lastName: string, firstName: string, middleName?: string): boolean {
  if (!lastName || !firstName) return false;

  const nameRegex = /^[а-яё\s-]+$/i;

  if (!nameRegex.test(lastName) || !nameRegex.test(firstName)) return false;
  if (middleName && !nameRegex.test(middleName)) return false;

  return true;
}

/**
 * Валидация телефона
 */
export function validatePhone(phone: string): boolean {
  if (!phone) return false;
  return /^\+7 \d{3} \d{3}-\d{2}-\d{2}$/.test(phone);
}

/**
 * Форматирование телефона
 */
export function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 11 && cleaned.startsWith("7")) {
    return `+7 ${cleaned.slice(1, 4)} ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(9, 11)}`;
  }
  return phone;
}

/**
 * Форматирование СНИЛС
 */
export function formatSNILS(snils: string): string {
  const cleaned = snils.replace(/\D/g, "");
  if (cleaned.length === 11) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 9)} ${cleaned.slice(9, 11)}`;
  }
  return snils;
}

/**
 * Генерация случайного ID
 */
export function generateId(): string {
  return Math.random().toString(36).substr(2, 9);
}

/**
 * Задержка выполнения
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Проверка полноты данных персоны
 */
export function calculateDataCompleteness(personData: PersonData): number {
  const fields = ["last_name", "first_name", "middle_name", "birth_date", "inn"];
  const filledFields = fields.filter((field) => personData[field as keyof PersonData]);
  return (filledFields.length / fields.length) * 100;
}

/**
 * Получение сокращенного текста
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
}

/**
 * Капитализация первой буквы
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Получение инициалов
 */
export function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

/**
 * Проверка является ли строка пустой
 */
export function isEmpty(str: string | null | undefined): boolean {
  return !str || str.trim().length === 0;
}

/**
 * Безопасное извлечение значения из объекта
 */
export function safeGet<T>(obj: unknown, path: string, defaultValue?: T): T | undefined {
  const keys = path.split(".");
  let current: unknown = obj;

  for (const key of keys) {
    if (current === null || current === undefined || typeof current !== "object") {
      return defaultValue;
    }
    current = (current as Record<string, unknown>)[key];
  }

  return current !== undefined ? (current as T) : defaultValue;
}

/**
 * Дебаунс функции
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Группировка массива по ключу
 */
export function groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
  return array.reduce(
    (groups, item) => {
      const groupKey = String(item[key]);
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
      return groups;
    },
    {} as Record<string, T[]>,
  );
}

/**
 * Сортировка массива объектов
 */
export function sortBy<T>(array: T[], key: keyof T, direction: "asc" | "desc" = "asc"): T[] {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];

    if (aVal < bVal) return direction === "asc" ? -1 : 1;
    if (aVal > bVal) return direction === "asc" ? 1 : -1;
    return 0;
  });
}

// Re-export variants
export * from "./variants";

// Re-export risk flags utilities
export * from "./riskFlags";

// Re-export date utilities
export * from "./dateUtils";

// Re-export logger utilities
export * from "./logger";
